import type { PublicSpot } from './types'

// Human-friendly spot name shown to standhouders (picker tooltips, /mijn-plek).
// The organiser curates a meaningful streetAddress for every spot (a house
// number, "Fluweelpark F3", "Kempstraat parking P11", "Hoek Virginistraat &
// Tortelduifstraat"…), so we show that verbatim. The internal code (id/label,
// e.g. FP006) is deliberately never surfaced to standhouders. Falls back to the
// label or id only if an address were ever empty.
export function spotDisplayName(spot: PublicSpot): string {
  return (spot.streetAddress ?? '').trim() || spot.label || spot.id
}

export function spotDisplayNameById(id: string, allSpots: PublicSpot[]): string {
  const spot = allSpots.find((s) => s.id === id)
  return spot ? spotDisplayName(spot) : id
}

// A spot has no real facade/house address when its curated address is a park
// position ("Fluweelpark F3"), a parking, a corner/description (hoek/overkant),
// a bare marker ("Kempstraat K0", "Linnenstraat L2", parking "P11") or has no
// house number at all. Those spots are marked on the ground on-site, so we show
// the "op de grond"-disclaimer for them; real house-number spots don't need it.
export function spotIsGroundMarked(spot: PublicSpot): boolean {
  const a = (spot.streetAddress ?? '').trim()
  const low = a.toLowerCase()
  if (low.includes('parking')) return true
  if (low.includes('hoek') || low.includes('overkant') || low.includes('naast')) return true
  if (a.includes('(')) return true
  if (/\b[FPKL]\d/.test(a)) return true
  if (low.includes('tws')) return true
  return !/\d/.test(a)
}

export function spotIsGroundMarkedById(id: string, allSpots: PublicSpot[]): boolean {
  const spot = allSpots.find((s) => s.id === id)
  return spot ? spotIsGroundMarked(spot) : false
}

// The same label, minus the street name the spot's zone already carries — for
// the printed kaart, where "Fluweelstraat Parkkant 41" on every polygon is
// unreadable noise while "41" fits and says the same thing on a per-zone blad.
// Only strips when the address really starts with the zone and something is
// left over, so "Hoek Virginistraat & Tortelduifstraat" stays intact.
export function spotShortName(spot: PublicSpot): string {
  const full = spotDisplayName(spot)
  const zone = (spot.zone ?? '').trim()
  if (!zone) return full
  if (full.toLowerCase().startsWith(zone.toLowerCase())) {
    const rest = full.slice(zone.length).trim()
    if (rest) return rest
  }
  return full
}
